import { useEffect, useId, useRef } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const availableControls = (dialog) =>
  Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(
    (element) =>
      !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
      !element.matches(':disabled') &&
      element.tabIndex >= 0
  );

const inertedElements = new WeakMap();

const acquireInert = (element) => {
  const existing = inertedElements.get(element);
  if (existing) {
    existing.count += 1;
    return;
  }
  inertedElements.set(element, {
    count: 1,
    inert: element.inert,
    ariaHidden: element.getAttribute('aria-hidden'),
  });
  element.inert = true;
  element.setAttribute('aria-hidden', 'true');
};

const releaseInert = (element) => {
  const state = inertedElements.get(element);
  if (!state) return;
  state.count -= 1;
  if (state.count > 0) return;
  element.inert = state.inert;
  if (state.ariaHidden == null) element.removeAttribute('aria-hidden');
  else element.setAttribute('aria-hidden', state.ariaHidden);
  inertedElements.delete(element);
};

const backgroundBranches = (dialog) => {
  const branches = [];
  let current = dialog;
  while (current?.parentElement && current.parentElement !== document.body) {
    for (const sibling of current.parentElement.children) {
      if (sibling !== current && sibling instanceof HTMLElement) branches.push(sibling);
    }
    current = current.parentElement;
  }
  return [...new Set(branches)];
};

/**
 * Applies the common modal contract: labelled dialog semantics, initial focus, a focus trap,
 * focus restoration, Escape handling, and an inert application background.
 */
export default function useAccessibleDialog({
  open,
  onClose,
  closeDisabled = false,
  initialFocusRef,
}) {
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);
  const onCloseRef = useRef(onClose);
  const closeDisabledRef = useRef(closeDisabled);
  onCloseRef.current = onClose;
  closeDisabledRef.current = closeDisabled;
  const generatedId = useId().replace(/:/g, '');
  const titleId = `dialog-title-${generatedId}`;

  useEffect(() => {
    if (!open || !dialogRef.current) return undefined;

    const dialog = dialogRef.current;
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const background = backgroundBranches(dialog);
    background.forEach(acquireInert);

    const focusFirst = () => {
      const preferred = initialFocusRef?.current;
      const target = preferred || availableControls(dialog)[0] || dialog;
      target.focus();
    };
    focusFirst();

    // Let controls inside the dialog consume Escape before dismissing their parent.
    const handleEscape = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || closeDisabledRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      onCloseRef.current();
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Tab') return;
      const focusable = availableControls(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === dialog)
      ) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keydown', handleEscape);
      background.forEach(releaseInert);
      const restore = previouslyFocused.current;
      if (restore?.isConnected) restore.focus();
    };
  }, [initialFocusRef, open]);

  return { dialogRef, titleId };
}
